import { useState } from 'react';

import { useDeals, useUpdateDeal } from '../../api/hooks';
import type { DealStage, DealView, OrgUserView } from '../../api/types';
import { Button, DataTable, Select, useToast } from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { formatCurrency, formatDate } from '../../lib/format';
import { DealModal } from './DealModal';
import { DEAL_STAGES, STAGE_LABELS } from './stages';

export interface CrmFilters {
  search: string;
  customerId: string;
  salesExecId: string;
  stage: DealStage | '';
}

interface PipelineTabProps {
  filters: CrmFilters;
  salesExecs: OrgUserView[];
}

function StageCell({ deal }: { deal: DealView }) {
  const { showToast } = useToast();
  const updateDeal = useUpdateDeal();

  async function handleStageChange(stage: DealStage) {
    try {
      await updateDeal.mutateAsync({ dealId: deal.id, input: { stage } });
      showToast(`Deal moved to ${STAGE_LABELS[stage]}`);
    } catch (error) {
      showToast(errorMessage(error, 'Could not update deal stage'), 'error');
    }
  }

  return (
    <Select
      aria-label={`Stage for ${deal.title}`}
      disabled={updateDeal.isPending}
      onChange={(event) => handleStageChange(event.target.value as DealStage)}
      style={{ minWidth: 170 }}
      value={deal.stage}
    >
      {DEAL_STAGES.map((stage) => (
        <option key={stage} value={stage}>
          {STAGE_LABELS[stage]}
        </option>
      ))}
    </Select>
  );
}

/** Pipeline: deals table with inline stage transitions and a New Deal modal. */
export function PipelineTab({ filters, salesExecs }: PipelineTabProps) {
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);

  const deals = useDeals({
    limit: 100,
    ...(filters.stage !== '' ? { stage: filters.stage } : {}),
    ...(filters.customerId !== '' ? { customerId: filters.customerId } : {}),
    ...(filters.salesExecId !== '' ? { salesExecId: filters.salesExecId } : {}),
    ...(filters.search.trim() !== '' ? { search: filters.search.trim() } : {}),
  });

  const columns: DataTableColumn<DealView>[] = [
    { key: 'title', header: 'Title' },
    { key: 'customerName', header: 'Customer' },
    { key: 'showName', header: 'Show' },
    { key: 'stage', header: 'Stage', render: (row) => <StageCell deal={row} /> },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => formatCurrency(row.amount),
    },
    { key: 'salesExecName', header: 'Sales Exec' },
    {
      key: 'expectedClose',
      header: 'Expected Close',
      render: (row) => formatDate(row.expectedClose),
    },
  ];

  return (
    <>
      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Deals ({deals.data?.data.length ?? 0})</h2>
          <Button onClick={() => setIsDealModalOpen(true)} type="button">
            New Deal
          </Button>
        </div>
        {deals.isLoading ? (
          <p className="rfp-loading">Loading deals…</p>
        ) : deals.isError ? (
          <p className="rfp-inline-error" role="alert">
            Could not load deals — please retry.
          </p>
        ) : (
          <DataTable
            columns={columns}
            emptyMessage="No deals match these filters"
            rowKey={(row) => row.id}
            rows={deals.data?.data ?? []}
          />
        )}
      </section>

      {isDealModalOpen && (
        <DealModal onClose={() => setIsDealModalOpen(false)} salesExecs={salesExecs} />
      )}
    </>
  );
}
